import { LegalTabs } from '../components/LegalTabs'
import { CONTACT_EMAIL, OPERATOR_NAME, TERMS_VERSION } from '../lib/constants'

export function TermsPage() {
  return (
    <section className="section legal-page">
      <div className="container narrow-container">
        <LegalTabs />
        <h1>Terms of service</h1>
        <p className="legal-document-meta">Version {TERMS_VERSION}</p>
        <p className="legal-intro">
          Rapid First Look is a free, independent service operated by {OPERATOR_NAME} in his
          personal capacity. It is not an official service of CNR or CNR-IMATI. By creating
          an account or submitting a manuscript, the user agrees to these Terms of Service.
        </p>

        <div className="legal-content">
          <h2>1. Nature of the service</h2>
          <p>
            The service provides private, informal feedback after a concentrated reading
            of approximately 15 minutes. It is intended to help an author understand how a
            scientific work may be received by an external researcher encountering it for
            the first time.
          </p>
          <p>
            The feedback may concern any aspect that stands out, including the idea,
            methodology, abstract, structure, figures, layout, formatting, or general
            presentation. Its depth depends on the subject and its proximity to the
            operator's expertise.
          </p>

          <h2>2. Accounts and acceptable use</h2>
          <p>
            Users must be at least 18 years old and capable of accepting these Terms. Each
            person may create only one account and is responsible for keeping their login
            credentials secure. Multiple accounts, automated submissions, spam, malicious
            files, and attempts to bypass the queue are prohibited.
          </p>

          <h2>3. No peer review or editorial value</h2>
          <p>
            The service is not peer review, scientific certification, an exhaustive
            correctness check, an editorial assessment, or a prediction of acceptance.
            It has no formal weight in any journal, conference, preprint, or other
            editorial procedure, including when the work has already been submitted.
          </p>
          <p>
            The author remains solely responsible for the manuscript, any changes made to
            it, its submission, and any decision based on the feedback.
          </p>
          <p>
            To the extent permitted by applicable law, the operator is not responsible for
            editorial outcomes or for decisions taken in reliance on the feedback. Nothing
            in these Terms excludes liability that cannot lawfully be excluded.
          </p>

          <h2>4. Eligibility and authorization</h2>
          <p>
            A work may be submitted only by an actual author or coauthor who is authorized
            by the other authors and any relevant institution or rights holder to share or
            reference it for this purpose. For a private upload, the user must have the right
            to transmit every included text, image, dataset, code fragment, and other element.
          </p>
          <p>
            Users may not submit another person's work when they are not an author, even
            with the stated intention of helping that person. The operator does not verify
            authorship in advance and may suspend or close a submission when its legitimacy
            is reasonably doubtful.
          </p>

          <h2>5. Manuscript sources and technical requirements</h2>
          <p>
            A manuscript may be submitted either by uploading a private PDF or by providing
            the identifier of a publicly available arXiv work.
          </p>
          <p>
            A private upload must be a PDF file no larger than 20 MB. A file may be rejected
            or left unopened if it is corrupted, encrypted, password-protected, technically
            invalid, malicious, or otherwise unsafe.
          </p>
          <p>
            For an arXiv-based submission, Rapid First Look stores the arXiv identifier and
            opens the manuscript directly on arXiv. It does not download or retain a manuscript
            copy in platform storage. The latest version available on arXiv at the time of
            reading will be used. An invalid, unavailable, withdrawn, or inaccessible arXiv
            record may be closed without substantive feedback.
          </p>

          <h2>6. Queue and timing</h2>
          <p>
            Each account may have one active submission. A replacement private PDF or a
            different arXiv work must be submitted as a new item. Eligible and technically
            accessible submissions are normally handled in first-submitted, first-examined
            order. There is no paid priority and no urgent lane.
          </p>
          <p>
            Public queue positions and estimated waiting times are indicative. They do not
            create a guaranteed deadline. New submissions may be temporarily suspended.
          </p>

          <h2>7. Signed or anonymized private PDFs</h2>
          <p>
            A private PDF may include author names and affiliations or may have identifying
            information removed by the user before upload. Rapid First Look does not alter or
            anonymize the file. The public queue is always anonymous and displays only a service
            code and status. The platform still processes the email address required for the
            account.
          </p>

          <h2>8. Limited permission to access the manuscript</h2>
          <p>
            The user retains all rights in the submitted material. For a private PDF, the user
            grants only a free, non-exclusive, temporary permission to receive, store, open,
            and read the file and to prepare the private feedback. For an arXiv submission,
            the user authorizes the operator to open and read the publicly available manuscript
            directly on arXiv for the same purpose.
          </p>
          <p>
            These permissions do not transfer ownership or grant a right to publish, distribute,
            exploit, reuse, or train any model on the work.
          </p>

          <h2>9. Confidentiality, no republication, and no AI</h2>
          <p>
            A private upload is not published, converted into a preprint, indexed, shared with
            other users, or used for the operator's research. An arXiv-based manuscript is
            already publicly available on arXiv, but Rapid First Look does not republish it or
            create a stored manuscript copy. Scientific content is not sent to or analyzed by
            generative artificial intelligence systems.
          </p>
          <p>
            Technical hosting, authentication, private storage, email, and security providers
            may process data only as needed to operate the platform and as described in the
            privacy notice.
          </p>

          <h2>10. Source handling and private feedback</h2>
          <p>
            An uploaded source PDF is deleted from active platform storage when the feedback
            or a final private service notice is published. Technical backup deletion may follow
            the retention cycle stated in the privacy notice. For an arXiv submission, no source
            manuscript copy is stored by Rapid First Look; the identifier remains associated with
            the submission record while the account remains active, unless the submission is
            withdrawn or the account is closed.
          </p>
          <p>
            Private feedback remains visible in the user's active account and may be downloaded
            as PDF. Closing the account removes active submissions, stored arXiv identifiers,
            private feedback, and account data, subject to technical backup cycles and any
            strictly necessary legal retention. Anonymous aggregate service statistics may remain.
          </p>

          <h2>11. One-way process</h2>
          <p>
            Rapid First Look is a one-way service. The platform does not provide rebuttals,
            replies, clarification requests, messaging, or iterative discussion. A user may
            contact the operator only for technical, privacy, security, or account problems.
          </p>

          <h2>12. No compensation or scientific recognition</h2>
          <p>
            No payment, donation, service in exchange, authorship, acknowledgement, citation,
            collaboration, access to results, or other scientific or professional benefit is
            requested. The process ends when the private feedback is published.
          </p>
          <p>
            A later request for deeper review, collaboration, or coauthorship may be initiated
            only by the user and is a separate matter. The operator remains free to decline.
          </p>

          <h2>13. Exceptions</h2>
          <p>
            A submission may be closed without substantive feedback if its source is unsafe,
            invalid, unavailable, or technically inaccessible; the user lacks authorization;
            the content is unlawful or abusive; or the work substantially overlaps with the
            operator's current research in a way that makes reading it inappropriate. The user
            receives a private notice. Any uploaded PDF is deleted.
          </p>

          <h2>14. Respectful but potentially critical feedback</h2>
          <p>
            Feedback will be directed at the work and written respectfully. It may nevertheless
            be negative or critical. Disagreement with the feedback does not create a right to a
            new first look, a response, or an amendment.
          </p>

          <h2>15. Suspension and termination</h2>
          <p>
            The operator may suspend registrations or submissions, perform maintenance, or end
            the service. If the service ends, active uploaded PDFs will be deleted and users will
            be given a reasonable opportunity to download existing feedback where technically
            possible. Rapid First Look has no control over the continued availability of content
            hosted by arXiv.
          </p>

          <h2>16. Changes to these Terms</h2>
          <p>
            Material changes to these Terms will be identified by a new version and effective
            date. A submission remains associated with the version accepted when it was
            submitted. Changes will not silently replace the terms applicable to an earlier
            submission.
          </p>

          <h2>17. Applicable law</h2>
          <p>
            These Terms are governed by Italian law, without prejudice to any mandatory
            protections applicable to the user. The competent court shall be determined in
            accordance with applicable law.
          </p>

          <h2>18. Contact</h2>
          <p>
            Technical, privacy, security, and account matters may be sent to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </div>
      </div>
    </section>
  )
}
